import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  TablePagination,
} from '@mui/material';
import { Close, Search } from '@mui/icons-material';
import { quickbooksService } from '../../../api/quickbooks';

const QBSyncDetailsDialog = ({ open, onClose, jobId }) => {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customerDetailsMap, setCustomerDetailsMap] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Get customer list from job details
  const customerList = useMemo(() => {
    if (!job?.details) return [];

    if (job.details.customer_ids && Array.isArray(job.details.customer_ids)) {
      return job.details.customer_ids;
    }
    if (job.details.customer_id) {
      return [job.details.customer_id];
    }
    return [];
  }, [job?.details]);

  // Map results by customer for easy lookup
  const resultsByCustomer = useMemo(() => {
    return job?.results || {};
  }, [job?.results]);

  // Get GL status for a customer
  const getGLStatus = useCallback((customerId) => {
    const tasks = resultsByCustomer[customerId];
    if (!tasks) return null;

    // Check various GL task types
    const glTask = tasks.general_ledger || tasks.general_ledger_cdc || tasks.general_ledger_build || tasks.general_ledger_fetch;
    if (glTask && glTask.status) {
      return glTask.status === 'success' || glTask.status === 'completed' ? 'success' : 'failed';
    }
    return null;
  }, [resultsByCustomer]);

  // Get TB status for a customer
  const getTBStatus = useCallback((customerId) => {
    const tasks = resultsByCustomer[customerId];
    if (!tasks) return null;

    const tbTask = tasks.trial_balance;
    if (tbTask && tbTask.status) {
      return tbTask.status === 'success' || tbTask.status === 'completed' ? 'success' : 'failed';
    }
    return null;
  }, [resultsByCustomer]);

  // Get customer name
  const getCustomerName = useCallback((customerId) => {
    const idKey = String(customerId);
    const cachedCustomer = customerDetailsMap[idKey];
    if (cachedCustomer?.name) {
      return cachedCustomer.name;
    }

    if (job?.results?.[customerId]?.customerDetails) {
      return job.results[customerId].customerDetails.name || `Customer ${customerId.substring(0, 8)}`;
    }

    return `Customer ${customerId.substring(0, 8)}`;
  }, [job?.results, customerDetailsMap]);

  // Get failed accounts (accounts where GL or TB failed)
  const failedAccounts = useMemo(() => {
    return customerList.filter((customerId) => {
      const glStatus = getGLStatus(customerId);
      const tbStatus = getTBStatus(customerId);
      return glStatus === 'failed' || tbStatus === 'failed';
    });
  }, [customerList, getGLStatus, getTBStatus]);

  // Filter failed accounts by search term
  const filteredFailedAccounts = useMemo(() => {
    if (!searchTerm.trim()) return failedAccounts;
    
    const searchLower = searchTerm.toLowerCase();
    return failedAccounts.filter((customerId) => {
      const name = getCustomerName(customerId);
      return name.toLowerCase().includes(searchLower);
    });
  }, [failedAccounts, searchTerm, getCustomerName]);

  // Reset page when search term changes
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  // Fetch job details
  const fetchJobDetails = useCallback(async () => {
    if (!jobId) return;

    setLoading(true);
    try {
      const response = await quickbooksService.getJobDetails(jobId);
      setJob(response.data);
    } catch (error) {
      console.error('Error fetching job details:', error);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  // Fetch customer details
  useEffect(() => {
    if (!job) return;

    const candidateIds = new Set();
    if (job.details?.customer_ids && Array.isArray(job.details.customer_ids)) {
      job.details.customer_ids.forEach((id) => candidateIds.add(String(id)));
    }
    if (job.details?.customer_id) {
      candidateIds.add(String(job.details.customer_id));
    }

    // Cache customer details from job results
    const jobProvidedCustomers = [];
    candidateIds.forEach((id) => {
      const detailsFromResult = job.results?.[id]?.customerDetails;
      if (detailsFromResult?.name) {
        jobProvidedCustomers.push({
          ...detailsFromResult,
          id: detailsFromResult.id ? String(detailsFromResult.id) : id,
        });
      }
    });

    if (jobProvidedCustomers.length > 0) {
      setCustomerDetailsMap((prev) => {
        const next = { ...prev };
        let changed = false;
        jobProvidedCustomers.forEach((customer) => {
          const key = String(customer.id);
          if (!next[key] || next[key].name !== customer.name) {
            next[key] = customer;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }

    // Fetch missing customer details
    const idsToFetch = Array.from(candidateIds).filter((id) => {
      if (customerDetailsMap[id]) return false;
      const detailsFromResult = job.results?.[id]?.customerDetails;
      return !(detailsFromResult && detailsFromResult.name);
    });

    if (idsToFetch.length === 0) return;

    let isCancelled = false;
    const fetchMissingCustomers = async () => {
      try {
        const responses = await Promise.allSettled(
          idsToFetch.map((id) => quickbooksService.getCustomerById(id))
        );

        if (isCancelled) return;

        const fetchedCustomers = responses
          .filter((result) => result.status === 'fulfilled' && result.value?.data)
          .map((result) => result.value.data)
          .filter((customer) => customer?.id);

        if (fetchedCustomers.length === 0) return;

        setCustomerDetailsMap((prev) => {
          const next = { ...prev };
          let changed = false;
          fetchedCustomers.forEach((customer) => {
            const key = String(customer.id);
            if (!next[key] || next[key].name !== customer.name) {
              next[key] = customer;
              changed = true;
            }
          });
          return changed ? next : prev;
        });
      } catch (error) {
        console.error('Error fetching customer details:', error);
      }
    };

    fetchMissingCustomers();
    return () => {
      isCancelled = true;
    };
  }, [job, customerDetailsMap]);

  // Fetch job details when dialog opens
  useEffect(() => {
    if (open && jobId) {
      fetchJobDetails();
      setSearchTerm('');
      setPage(0);
    }
  }, [open, jobId, fetchJobDetails]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const hour12 = String(hours % 12 || 12).padStart(2, '0');
      return `${month}/${day}/${year}, ${hour12}:${minutes}:${seconds} ${ampm}`;
    } catch {
      return '—';
    }
  };

  // Render status chip
  const renderStatusChip = (status) => {
    if (status === 'success') {
      return (
        <Chip
          label="Success"
          size="small"
          sx={{ 
            fontWeight: 500,
            backgroundColor: '#d1fae5',
            color: '#065f46',
          }}
        />
      );
    } else if (status === 'failed') {
      return (
        <Chip
          label="Failed"
          size="small"
          sx={{ 
            fontWeight: 500,
            backgroundColor: '#fee2e2',
            color: '#991b1b',
          }}
        />
      );
    }
    return (
      <Chip
        label="—"
        size="small"
        sx={{ backgroundColor: '#e5e7eb', color: '#6b7280' }}
      />
    );
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get paginated accounts
  const paginatedAccounts = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredFailedAccounts.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredFailedAccounts, page, rowsPerPage]);

  // Check if single account
  const isSingleAccount = customerList.length === 1;
  const singleAccountId = isSingleAccount ? customerList[0] : null;
  const singleAccountName = isSingleAccount ? getCustomerName(singleAccountId) : null;

  // Check if all accounts succeeded (both GL and TB are success for all accounts)
  const allAccountsSucceeded = useMemo(() => {
    if (customerList.length === 0) return false;
    return customerList.every((customerId) => {
      const glStatus = getGLStatus(customerId);
      const tbStatus = getTBStatus(customerId);
      return glStatus === 'success' && tbStatus === 'success';
    });
  }, [customerList, getGLStatus, getTBStatus]);

  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      maxWidth="md"
      keepMounted
      sx={{
        zIndex: 200001,
      }}
      slotProps={{
        backdrop: { sx: { zIndex: 200000 } },
      }}
      container={typeof document !== 'undefined' ? document.body : undefined}
      PaperProps={{
        sx: {
          borderRadius: 2,
          zIndex: 200001,
          width: '100%',
          maxWidth: 600,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          pb: 2,
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {isSingleAccount ? `Sync Details - ${singleAccountName}` : `Sync Detail - ${job?.details?.customer_ids?.length > 1 ? 'Bulk Sync' : 'Single Sync'}`}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: '#6b7280' }}
          >
            <Close />
          </IconButton>
        </Box>
        {jobId && (
          <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 400 }}>
            Job ID: {jobId}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 3, minHeight: 300 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Box>
        ) : job ? (
          <Box>
            {/* Primary Fields */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#374151', fontSize: '1rem' }}>
                Primary Fields
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#6b7280', mb: 0.5 }}>
                    Started At
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatDate(job.started_at)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: '#6b7280', mb: 0.5 }}>
                    Completed At
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatDate(job.completed_at)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: '#6b7280', mb: 0.5 }}>
                    Initiated By
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {job.user_full_name || job.user_email || job.user || '—'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Sync Overview - Show for single account or when all accounts succeeded */}
            {(isSingleAccount || allAccountsSucceeded) ? (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#374151', fontSize: '1rem' }}>
                  Sync Overview
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4, alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500, minWidth: 140 }}>
                      General Ledger:
                    </Typography>
                    {isSingleAccount ? (
                      renderStatusChip(getGLStatus(singleAccountId))
                    ) : (
                      renderStatusChip('success')
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500, minWidth: 140 }}>
                      Trial Balance:
                    </Typography>
                    {isSingleAccount ? (
                      renderStatusChip(getTBStatus(singleAccountId))
                    ) : (
                      renderStatusChip('success')
                    )}
                  </Box>
                </Box>
              </Box>
            ) : (
              /* Multiple Accounts: Failed Accounts Table */
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#374151', fontSize: '1rem' }}>
                      Failed Accounts
                    </Typography>
                    {failedAccounts.length > 0 && (
                      <Chip
                        label={failedAccounts.length}
                        size="small"
                        sx={{ 
                          fontWeight: 600,
                          backgroundColor: '#fee2e2',
                          color: '#991b1b',
                        }}
                      />
                    )}
                  </Box>
                  {failedAccounts.length > 0 && (
                    <TextField
                      variant="standard"
                      size="small"
                      placeholder="Search here..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search sx={{ color: '#9ca3af', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        width: 300,
                        '& .MuiInput-underline:before': {
                          borderBottomColor: '#e5e7eb',
                        },
                        '& .MuiInput-underline:hover:before': {
                          borderBottomColor: '#d1d5db',
                        },
                        '& .MuiInput-underline:after': {
                          borderBottomColor: '#627CFB',
                        },
                      }}
                    />
                  )}
                </Box>

                {filteredFailedAccounts.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      {failedAccounts.length === 0
                        ? 'No failed accounts'
                        : 'No accounts match your search'}
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer sx={{ boxShadow: 'none' }}>
                    <Table size="small" sx={{ borderCollapse: 'collapse' }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                          <TableCell sx={{ fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>ACCOUNT</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>GENERAL LEDGER</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>TRIAL BALANCE</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedAccounts.map((customerId) => {
                          const glStatus = getGLStatus(customerId);
                          const tbStatus = getTBStatus(customerId);
                          return (
                            <TableRow key={customerId} hover>
                              <TableCell sx={{ fontWeight: 500, borderBottom: '1px solid #e5e7eb' }}>{getCustomerName(customerId)}</TableCell>
                              <TableCell sx={{ borderBottom: '1px solid #e5e7eb' }}>{renderStatusChip(glStatus)}</TableCell>
                              <TableCell sx={{ borderBottom: '1px solid #e5e7eb' }}>{renderStatusChip(tbStatus)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    <TablePagination
                      component="div"
                      count={filteredFailedAccounts.length}
                      page={page}
                      onPageChange={handleChangePage}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[5]}
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                      sx={{
                        borderTop: '1px solid #e5e7eb',
                        '& .MuiTablePagination-toolbar': {
                          paddingLeft: 2,
                          paddingRight: 2,
                        },
                        '& .MuiTablePagination-selectLabel': {
                          display: 'none',
                        },
                        '& .MuiTablePagination-displayedRows': {
                          margin: 0,
                        },
                      }}
                    />
                  </TableContainer>
                )}
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Unable to load job details
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e5e7eb' }}>
        <Box sx={{ flex: 1 }} />
      </DialogActions>
    </Dialog>
  );
};

export default QBSyncDetailsDialog;
